import { Drawer, Grid2, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { StandardButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { toastr } from "../../_components/toastr/toastr";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { newsService } from "../../_services/news.service";
import { INewsManage } from "../../_common/models/news/newsManage.model";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";
import { DataTypeAutoComplete } from "../../_components/autocomplete/dataType.autocomplete";
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import { FileTypeEnum } from "../../_common/enum/fileType.enum";
import { NewsFileList } from "./news-fileList";
import EnumSelect from "../../_components/autocomplete/enum.select";
import { NewsFileTypeEnum } from "../../_common/enum/newsFileType.enum";
import { NewsStatusEnum } from "../../_common/enum/newsStatus.enum";
import { accountService } from "../../_services/accounts.service";

export const NewsManageDrawer = (params: { isOpen?: boolean, onSave(): void, onClose(): void }) => {
    const { newsId } = useParams<{ newsId: string }>();

    const resetData = (): INewsManage => ({
        id: newsId ? Number(newsId) : 0,
        newsCategoryId: '',
        centreId: '',
        description: '',
        isActive: true,
        file: null,
        newsFileTypeId: null,
        statusId: null
    });

    const [data, setData] = useState<INewsManage>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const editorRef = useRef<TinyMCEEditor | null>(null);
    const [upload, setUpload] = useState<{ id: number | null, file: any | null }>(resetData());


    useEffect(() => {
        if (newsId != null)
            fetchData();
        else
            setData(resetData);

    }, [newsId]);

    useEffect(() => {
        if (isOpen == false)
            setIsOpen(true);
    }, []);

    function onUploadChange(event: any) {
        setUpload((prev: any) => {
            const _upload = prev
            _upload.file = event.target.files[0];
            return _upload
        })

    }

    function fetchData() {
        if (!newsId) return;
        setIsLoading(true);
        return newsService
            .get(newsId)
            .then(
                (json: INewsManage) => {
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        const formData = new FormData();
        formData.append("file", upload.file);
        formData.append("fileTypeId", FileTypeEnum.NewsFile.toString());
        formData.append("newsCategoryId", data.newsCategoryId);
        formData.append("centreId", data.centreId);
        formData.append("description", data.description);
        if (data.newsFileTypeId != null)
            formData.append("newsFileTypeId", String(data.newsFileTypeId));

        if (newsId != null)
            formData.append("id", newsId);
        return newsService
            .submit(formData)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!newsId) return;
        setIsLoading(true);
        return newsService
            .disable(newsId)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    function approve() {
        if (!newsId) return;
        setIsLoading(true);
        return newsService
            .approve(newsId)
            .then(
                () => {
                    toastr.success("Approved successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function reject() {
        if (!newsId) return;
        setIsLoading(true);
        return newsService
            .reject(newsId)
            .then(
                () => {
                    toastr.success("Rejected successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }


    const handleEditorChange = (content: any, editor: any) => {
        setData((_description: any) => {
            return {
                ..._description,
                description: content
            }
        });
    };


    const tinyMceSettings = {
        content_style: "body {font-family:Helvetica;}",
        height: 500,
        menubar: 'file edit view insert format table help',
        statusbar: false,
        placeholder: 'Enter description here...',
        plugins: 'preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media template codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons accordion',
        toolbar: "undo redo | bold italic underline strikethrough | align numlist bullist | blocks fontfamily fontsize | link image | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | table media | accordion accordionremove | code fullscreen preview | save print | pagebreak anchor codesample | ltr rtl"
    };

    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={isOpen}
            ModalProps={{
                keepMounted: true
            }}>

            <DrawerHeader name={(!!newsId) ? "Update News" : "Create News"} onClose={params.onClose} />

            <ValidatorForm
                onSubmit={submit}
                noValidate>

                <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                    <Grid2 size={{ xs: 12 }}>
                        <DataTypeAutoComplete
                            name="newsCategoryId"
                            label="News Category"
                            required
                            value={data.newsCategoryId}
                            onChange={handleChange} dataType={DataTypeEnum.NewsCategorys} />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <DataTypeAutoComplete
                            name="centreId"
                            label="Centre"
                            required
                            value={data.centreId}
                            onChange={handleChange} dataType={DataTypeEnum.Centres} />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Editor
                            tagName='description'
                            textareaName='description'
                            onInit={(_evt, editor) => editorRef.current = editor}
                            value={data.description}
                            apiKey='5ea9ppzcmew36gv61axb9ztq1tyi8liqm12ht880on2wf3yh'

                            disabled={false}
                            init={tinyMceSettings}
                            onEditorChange={handleEditorChange} />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <EnumSelect
                            enumObject={NewsFileTypeEnum}
                            label="File Type"
                            name="newsFileTypeId"
                            value={data.newsFileTypeId}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            label="File to Upload"
                            type="file"
                            id="file"
                            fullWidth
                            onChange={onUploadChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid2>
                    {newsId && (
                        <NewsFileList />
                    )}


                    <Grid2 size={{ xs: 12 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: !data.id ? 12 : data?.isActive === false ? 12 : 6 }}>
                                <StandardButton
                                    saving={saving}
                                    loading={isLoading}
                                    icon="save"
                                    type="submit">
                                    {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                </StandardButton>
                            </Grid2>

                            {(!!newsId && data?.isActive === true) &&
                                <Grid2 size={{ xs: 6 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="block"
                                        variant="error"
                                        onClick={disable}>
                                        Disable
                                    </StandardButton>
                                </Grid2>
                            }
                        </Grid2>
                    </Grid2>
                    {data.statusId === NewsStatusEnum.Pending && accountService.isAdmin && <Grid2 size={{ xs: 12 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: 6 }}>
                                <StandardButton icon={"check"} loading={isLoading} saving={saving} onClick={() => { approve() }} variant="success" type="button">Approve</StandardButton>
                            </Grid2>
                            <Grid2 size={{ xs: 6 }}>
                                <StandardButton icon={"clear"} loading={isLoading} saving={saving} onClick={() => { reject() }} variant="error" type="button">Reject</StandardButton>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                    }
                </Grid2>

            </ValidatorForm>
        </Drawer>
    );
};